// @ts-nocheck -- Gadget Client types are stale; will resolve after ggt push regenerates types
/**
 * File: web/routes/_auth.sign-in.tsx
 * Purpose: Magic link + Google OAuth sign-in, powered by @barrykilby/auth.
 */

import type { ActionFunctionArgs } from "react-router";
import { useActionData, useNavigation, useSearchParams } from "react-router";
import { MagicLinkAuth, sendMagicLinkForEmail } from "@barrykilby/auth";

export async function loader() {
  return {};
}

export async function action({ request, context }: ActionFunctionArgs) {
  const formData = await request.formData();
  const email = formData.get("email") as string;
  const inviteTokenRaw = (formData.get("inviteToken") as string | null) ?? null;
  const inviteToken =
    inviteTokenRaw && inviteTokenRaw.length > 0 ? inviteTokenRaw : null;

  const { randomUUID } = await import("node:crypto");
  const result = await sendMagicLinkForEmail(context, email, {
    onCreateUser: () => ({ uuid: randomUUID() }),
  });

  // Carry the invite token through to the magic-link URL so the
  // post-auth redirect routes accordingly. Subcontract share-tokens
  // were retired in the collapse (#104); the token now only matters
  // for membership invites (mb_<hex> prefix).
  if (result.ok && inviteToken) {
    try {
      const adminApi = context.api.actAsAdmin;
      const existing = await adminApi.user.findMany({
        filter: { email: { equals: result.email } },
        select: { id: true },
        first: 1,
      });
      if (existing[0]?.id) {
        await (adminApi.user as any).sendMagicLink(existing[0].id, {
          inviteToken,
        });
      }
    } catch {
      // Non-critical fallback to inbox redirect.
    }
  }

  return result;
}

export default function SignIn() {
  const actionData = useActionData<typeof action>();
  const navigation = useNavigation();
  const [searchParams] = useSearchParams();
  const isSubmitting = navigation.state === "submitting";

  // Force Google's account-picker every time so users can switch
  // Google accounts after signing out of Manos. Without this, Google's
  // SSO silently reuses whichever account was last signed-in, which
  // means a "sign out" of Manos doesn't actually let you pick a
  // different Google account on the next sign-in. Gadget's
  // `/auth/google/start` forwards arbitrary query params through to
  // the Google authorisation URL.
  const oauthParams = new URLSearchParams(searchParams);
  oauthParams.set("prompt", "select_account");
  const oauthSearch = `?${oauthParams.toString()}`;
  const search = searchParams.toString() ? `?${searchParams.toString()}` : "";
  const inviteToken = searchParams.get("invite") || "";

  return (
    <MagicLinkAuth
      appName="Manos"
      mode="sign-in"
      googleOAuthHref={`/auth/google/start${oauthSearch}`}
      crossLinkHref={`/sign-up${search}`}
      actionData={actionData}
      isSubmitting={isSubmitting}
      showAppNameInHeading={false}
      extraFields={
        inviteToken ? (
          <input
            type="hidden"
            name="inviteToken"
            value={inviteToken}
            data-testid="subcontract-invite-token"
          />
        ) : undefined
      }
    />
  );
}
